import { useEffect, useState } from 'react';

import { send } from '@actual-app/core/platform/client/connection';

import type { CloudApiClient, CloudBackup, CloudBudget } from './api-client';
import { loadCloudBudgetId, saveCloudBudgetId } from './budget-cache';
import {
  describeSnapshot,
  downloadSnapshot,
  uploadSnapshot,
} from './snapshot-transfer';

type BackupState = Readonly<{
  backups: readonly CloudBackup[];
  budgets: readonly CloudBudget[];
  error: Error | null;
  selectedBudgetId: string | null;
  status: 'idle' | 'loading' | 'ready' | 'error';
  verifiedBackupIds: ReadonlySet<string>;
}>;

export type CloudBackups = BackupState &
  Readonly<{
    createBackup: () => Promise<CloudBackup>;
    downloadBackup: (backup: CloudBackup) => Promise<ArrayBuffer>;
    reload: () => Promise<void>;
    restoreBackupAsCopy: (
      backup: CloudBackup,
      copyName: string,
    ) => Promise<void>;
    selectBudget: (budgetId: string) => Promise<void>;
  }>;

export function useCloudBackups(
  api: CloudApiClient,
  subject: string,
  workspaceId: string | null,
  localBudgetId: string | null,
  localBudgetName: string | null,
  isOffline: boolean,
  isEndToEndEncrypted = false,
): CloudBackups {
  const [state, setState] = useState<BackupState>(emptyState);

  async function load(): Promise<void> {
    if (workspaceId === null || localBudgetId === null || isOffline) {
      setState(emptyState);
      return;
    }
    setState(current => ({ ...current, error: null, status: 'loading' }));
    try {
      const budgets = await api.listBudgets(workspaceId);
      const selectedBudgetId = loadCloudBudgetId(
        subject,
        workspaceId,
        localBudgetId,
        budgets,
      );
      const backups =
        selectedBudgetId === null
          ? []
          : await api.listBackups(workspaceId, selectedBudgetId);
      setState(current => ({
        ...current,
        backups,
        budgets,
        error: null,
        selectedBudgetId,
        status: 'ready',
      }));
    } catch (error) {
      setState(current => ({
        ...current,
        error: toError(error),
        status: 'error',
      }));
    }
  }

  useEffect(() => {
    let isCurrent = true;
    if (workspaceId === null || localBudgetId === null || isOffline) {
      setState(emptyState);
      return;
    }
    setState(current => ({ ...current, error: null, status: 'loading' }));
    void api
      .listBudgets(workspaceId)
      .then(async budgets => {
        const selectedBudgetId = loadCloudBudgetId(
          subject,
          workspaceId,
          localBudgetId,
          budgets,
        );
        const backups =
          selectedBudgetId === null
            ? []
            : await api.listBackups(workspaceId, selectedBudgetId);
        if (!isCurrent) return;
        setState(current => ({
          ...current,
          backups,
          budgets,
          error: null,
          selectedBudgetId,
          status: 'ready',
        }));
      })
      .catch(error => {
        if (!isCurrent) return;
        setState(current => ({
          ...current,
          error: toError(error),
          status: 'error',
        }));
      });
    return () => {
      isCurrent = false;
    };
  }, [api, isOffline, localBudgetId, subject, workspaceId]);

  async function selectBudget(budgetId: string): Promise<void> {
    if (workspaceId === null || localBudgetId === null || isOffline) return;
    const selected = state.budgets.find(
      budget => budget.id === budgetId && budget.status === 'active',
    );
    if (!selected) return;
    saveCloudBudgetId(subject, workspaceId, localBudgetId, budgetId);
    setState(current => ({ ...current, status: 'loading' }));
    try {
      const backups = await api.listBackups(workspaceId, budgetId);
      setState(current => ({
        ...current,
        backups,
        error: null,
        selectedBudgetId: budgetId,
        status: 'ready',
      }));
    } catch (error) {
      setState(current => ({
        ...current,
        error: toError(error),
        status: 'error',
      }));
    }
  }

  async function createBackup(): Promise<CloudBackup> {
    if (
      workspaceId === null ||
      localBudgetId === null ||
      localBudgetName === null ||
      isOffline ||
      isEndToEndEncrypted
    ) {
      throw new Error('Cloud backup is unavailable for this local budget.');
    }
    let budgetId = state.selectedBudgetId;
    if (budgetId === null) {
      const activeBudgets = state.budgets.filter(
        budget => budget.status === 'active',
      );
      if (activeBudgets.length > 0) {
        throw new Error('Select the cloud budget before creating a backup.');
      }
      const createdBudget = await api.createBudget(workspaceId, {
        name: localBudgetName,
      });
      budgetId = createdBudget.id;
      saveCloudBudgetId(subject, workspaceId, localBudgetId, budgetId);
      setState(current => ({
        ...current,
        budgets: [...current.budgets, createdBudget],
        selectedBudgetId: createdBudget.id,
      }));
    }

    const exported = await send('export-budget');
    if ('error' in exported || exported.data === null) {
      throw new Error('Local budget export failed.');
    }
    const description = await describeSnapshot(exported.data);
    const upload = await api.createBackup(workspaceId, budgetId, description);
    await uploadSnapshot(upload.upload, exported.data);
    await api.getBackupDownload(workspaceId, budgetId, upload.backup.id);
    const backups = await api.listBackups(workspaceId, budgetId);
    setState(current => ({
      ...current,
      backups,
      error: null,
      selectedBudgetId: budgetId,
      status: 'ready',
      verifiedBackupIds: new Set([
        ...current.verifiedBackupIds,
        upload.backup.id,
      ]),
    }));
    return upload.backup;
  }

  async function downloadBackup(backup: CloudBackup): Promise<ArrayBuffer> {
    if (workspaceId === null || state.selectedBudgetId === null || isOffline) {
      throw new Error('Cloud backup download requires connectivity.');
    }
    const result = await api.getBackupDownload(
      workspaceId,
      state.selectedBudgetId,
      backup.id,
    );
    const bytes = await downloadSnapshot(result.download, result.backup);
    setState(current => ({
      ...current,
      verifiedBackupIds: new Set([...current.verifiedBackupIds, backup.id]),
    }));
    return bytes;
  }

  async function restoreBackupAsCopy(
    backup: CloudBackup,
    copyName: string,
  ): Promise<void> {
    const bytes = await downloadBackup(backup);
    const result = await send('import-budget', {
      asCopy: true,
      buffer: bytes,
      copyName,
      filename: 'budget-backup.zip',
      type: 'actual',
    });
    if (result.error) throw new Error(result.error);
  }

  return {
    ...state,
    createBackup,
    downloadBackup,
    reload: load,
    restoreBackupAsCopy,
    selectBudget,
  };
}

const emptyState: BackupState = {
  backups: [],
  budgets: [],
  error: null,
  selectedBudgetId: null,
  status: 'idle',
  verifiedBackupIds: new Set(),
};

function toError(value: unknown): Error {
  return value instanceof Error ? value : new Error('Unexpected backup error.');
}
